"use client";

import React, { useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useURLFilter } from "./useCategoryFilter";
import { Range, getTrackBackground } from "react-range";
import { Star, RotateCcw, Calendar, Tag } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const MIN = 100;
const MAX = 5000;
const STEP = 100;

interface Props {
  tripTypes: any[];
  duration: any[];
}

const PriceRangeSection = ({ tripTypes, duration }: Props) => {
  const { setFilter, getFilter } = useURLFilter();
  const router = useRouter();
  const pathname = usePathname();

  const [values, setValues] = useState<number[]>([
    Number(getFilter("min_price") || MIN),
    Number(getFilter("max_price") || MAX),
  ]);

  const tripType = getFilter("trip_type", "");
  const selectedMinRating = Number(getFilter("min_rating") || 0);
  const activeDuration = getFilter("duration", "");

  const handleSliderChange = (vals: number[]) => {
    setValues(vals);
    setFilter("min_price", vals[0]);
    setFilter("max_price", vals[1]);
  };

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-y-6 lg:gap-x-0 items-end">
        <div className="lg:col-span-2 space-y-4 lg:pr-8">
          <div className="flex justify-between items-center px-1">
            <label className="text-xs font-black text-gray-500 uppercase tracking-widest">
              Budget
            </label>
            <span className="text-xs font-bold text-primary-600">
              Rs.{values[0]}-{values[1]}
            </span>
          </div>
          <div className="px-2 py-2">
            <Range
              step={STEP}
              min={MIN}
              max={MAX}
              values={values}
              onChange={handleSliderChange}
              renderTrack={({ props, children }) => (
                <div
                  {...props}
                  className="h-1 w-full rounded-full"
                  style={{
                    background: getTrackBackground({
                      values,
                      colors: ["#f9c02f", "#7c3aed", "#f9c02f"],
                      min: MIN,
                      max: MAX,
                    }),
                  }}
                >
                  {children}
                </div>
              )}
              renderThumb={({ props }) => (
                <div
                  {...props}
                  className="h-3.5 w-3.5 rounded-full bg-white border-2 border-primary-500 shadow-sm outline-none transition-transform active:scale-125"
                />
              )}
            />
          </div>
        </div>

        <div className="space-y-3 lg:px-4">
          <label className="text-xs font-black text-gray-500 uppercase tracking-widest px-1">
            Rating
          </label>
          <div className="flex gap-1 items-center justify-start h-10 rounded-xl">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                onClick={() =>
                  setFilter("min_rating", selectedMinRating === n ? null : n)
                }
                className="transition-transform active:scale-90"
              >
                <Star
                  size={24}
                  className={`${selectedMinRating >= n ? "fill-amber-400 text-amber-400" : "text-gray-200"}`}
                />
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-3 lg:px-4">
          <label className="text-xs font-black text-gray-500 uppercase tracking-widest px-1">
            Duration
          </label>
          <Select
            value={activeDuration || "all"}
            onValueChange={(val) =>
              setFilter("duration", val === "all" ? null : val)
            }
          >
            <SelectTrigger className="w-full h-10 border-gray-200 rounded-xl text-sm font-bold text-gray-600 focus:ring-1 focus:ring-primary-500">
              <div className="flex items-center gap-2">
                <Calendar size={13} className="text-gray-500" />
                <SelectValue placeholder="Days" />
              </div>
            </SelectTrigger>
            <SelectContent className="rounded-xl border-gray-200">
              <SelectItem value="all" className="text-sm">
                Any Duration
              </SelectItem>
              {duration.map((d) => (
                <SelectItem key={d} value={String(d)} className="text-sm">
                  {d} Days
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-3 lg:px-4">
          <label className="text-xs font-black text-gray-500 uppercase tracking-widest px-1">
            Category
          </label>
          <Select
            value={tripType || "all"}
            onValueChange={(val) =>
              setFilter("trip_type", val === "all" ? null : val)
            }
          >
            <SelectTrigger className="w-full h-10 border border-gray-200 rounded-xl text-sm font-bold text-gray-600 focus:ring-1 focus:ring-primary-500">
              <div className="flex items-center gap-2">
                <Tag size={13} className="text-gray-500" />
                <SelectValue placeholder="Type" />
              </div>
            </SelectTrigger>
            <SelectContent className="rounded-xl border-gray-200">
              <SelectItem value="all" className="text-sm">
                All Types
              </SelectItem>
              {tripTypes.map((item) => (
                <SelectItem key={item.id} value={item.slug} className="text-sm">
                  {item.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex gap-2 h-10 lg:pl-4">
          <button
            onClick={() => router.push(pathname, { scroll: false })}
            className="flex-1 flex items-center justify-center gap-2 text-gray-700 border border-gray-200 text-xs font-black rounded-xl hover:bg-gray-100 transition-all active:scale-[0.98] uppercase tracking-wider"
          >
            <RotateCcw size={16} />
            Reset
          </button>
        </div>
      </div>
    </div>
  );
};

export default PriceRangeSection;
